import { FunctionComponent, memo, useCallback, useMemo } from 'react';
import { StoryCardType } from '../../../../services';
import { getCssVar } from '../../../utils';
import { IconLabel } from '../../IconLabel';
import { LineClamp, LineClampProps } from '../../LineClamp';
import { PinnedIcon } from '../../PinnedIcon';

import classes from './HorizontalStoryFeedContentCommon.module.scss';

interface HorizontalStoryFeedContentCommonProps {
  isPinnedToTopOfNewsFeed: boolean;
  isPinnedToTopOfStory: boolean;
  cardType?: StoryCardType;
  source?: string;
  title?: string;
  subTitle?: string;
  text?: string;
  htmlText?: string;
}

export const HorizontalStoryFeedContentCommon: FunctionComponent<HorizontalStoryFeedContentCommonProps> =
  memo(
    ({
      isPinnedToTopOfNewsFeed,
      isPinnedToTopOfStory,
      cardType,
      source,
      title,
      subTitle,
      text,
      htmlText,
    }) => {
      const lineClampCommonProps = useMemo(() => {
        return {
          maxLines: 3,
          readMore: '...',
          readMoreClassName: classes['horizontal-card-content__read-more'],
        } as LineClampProps;
      }, []);

      const getIcon = useCallback(
        (className?: string) => {
          if (![StoryCardType.ARTICLE, StoryCardType.POST].includes(cardType as StoryCardType)) {
            return;
          }

          return (
            <IconLabel
              className={className}
              iconId={`arrow-rounded-right${cardType === StoryCardType.ARTICLE ? '-up' : ''}`}
              iconSize={14}
              color={getCssVar('--card-content-headline-icon-color')}
              singleColor
            />
          );
        },
        [cardType]
      );

      const renderSource = useMemo(() => {
        if (!source && !isPinnedToTopOfNewsFeed && !isPinnedToTopOfStory) {
          return null;
        }

        return (
          <div className={classes['horizontal-card-content__source-wrapper']}>
            <PinnedIcon
              isPinnedToTopOfNewsFeed={isPinnedToTopOfNewsFeed}
              isPinnedToTopOfStory={isPinnedToTopOfStory}
              color={getCssVar('--base-link-text-color')}
              source={source}
            />

            {source && <div className={classes['horizontal-card-content__source']}>{source}</div>}
          </div>
        );
      }, [isPinnedToTopOfNewsFeed, isPinnedToTopOfStory, source]);

      const renderTitle = useMemo(() => {
        if (!title) {
          return null;
        }

        return (
          <LineClamp
            {...lineClampCommonProps}
            maxLines={2}
            stringContent={title}
            stringContentIcon={getIcon(classes['horizontal-card-content__title-icon'])}
            readMore={<>...{getIcon(classes['horizontal-card-content__read-more-icon'])}</>}
            contentClassName={classes['horizontal-card-content__title']}
          />
        );
      }, [getIcon, lineClampCommonProps, title]);

      const renderSubTitle = useMemo(() => {
        if (!subTitle) {
          return null;
        }

        return (
          <LineClamp
            {...lineClampCommonProps}
            stringContent={subTitle}
            contentClassName={classes['horizontal-card-content__subTitle']}
          />
        );
      }, [lineClampCommonProps, subTitle]);

      const renderText = useMemo(() => {
        if (subTitle || htmlText || !text) {
          return null;
        }

        return (
          <LineClamp
            {...lineClampCommonProps}
            stringContent={text}
            contentClassName={classes['horizontal-card-content__text']}
            readMoreClassName={classes['horizontal-card-content__read-more-text']}
          />
        );
      }, [htmlText, lineClampCommonProps, subTitle, text]);

      const renderHtml = useMemo(() => {
        if (subTitle || !htmlText) {
          return null;
        }

        return (
          <LineClamp
            {...lineClampCommonProps}
            htmlStringContent={htmlText}
            contentClassName={classes['horizontal-card-content__text']}
            readMoreClassName={classes['horizontal-card-content__read-more-text']}
          />
        );
      }, [htmlText, lineClampCommonProps, subTitle]);

      return (
        <div className={classes['horizontal-card-content']}>
          {renderSource}

          {renderTitle}

          {renderSubTitle}

          {renderText}

          {renderHtml}
        </div>
      );
    }
  );
